import { useState } from "react";
import { useGetList, useGetOne } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { HardHat, Plus, Star } from "lucide-react";
import { JobAssignSubDialog } from "./JobAssignSubDialog";

interface Assignment {
  id: string;
  subcontractor_id: string;
  status: string;
  role_description: string;
  rating: number | null;
  was_on_time: boolean | null;
  created_at: string;
}

interface Sub {
  id: string;
  first_name: string;
  last_name: string;
  company_name: string;
}

interface JobAssignmentListProps {
  jobId: string;
  contactId: string | number;
  readOnly?: boolean;
}

function AssignmentRow({ assignment }: { assignment: Assignment }) {
  const { data: sub } = useGetOne<Sub>("subcontractors", {
    id: assignment.subcontractor_id,
  });
  const name = sub
    ? `${sub.first_name} ${sub.last_name}`.trim() || sub.company_name
    : "Loading…";

  return (
    <Card className="shadow-none">
      <CardContent className="p-2.5 flex items-center gap-3">
        <HardHat className="w-4 h-4 text-muted-foreground shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{name}</p>
          {assignment.role_description && (
            <p className="text-xs text-muted-foreground">{assignment.role_description}</p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs px-1.5 py-0.5 rounded" style={{
            color: assignment.status === "completed" ? "#16a34a" : "#2563eb",
            backgroundColor: assignment.status === "completed" ? "#dcfce7" : "#dbeafe",
          }}>
            {assignment.status}
          </span>
          {assignment.rating && (
            <span className="flex items-center gap-0.5 text-xs font-medium">
              <Star className="w-3 h-3 text-amber-500" />
              {Number(assignment.rating).toFixed(1)}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function JobAssignmentList({ jobId, contactId, readOnly = false }: JobAssignmentListProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data, isPending } = useGetList<Assignment>("job_assignments", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "created_at", order: "DESC" },
    filter: { job_id: jobId },
  });

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold flex items-center gap-1.5">
          <HardHat className="w-4 h-4" />
          Subcontractors
          {data && data.length > 0 && (
            <span className="text-xs text-muted-foreground font-normal">({data.length})</span>
          )}
        </h4>
        {!readOnly && (
          <button
            onClick={() => setDialogOpen(true)}
            className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-blue-600 hover:bg-blue-50"
          >
            <Plus className="w-3 h-3" /> Assign
          </button>
        )}
      </div>

      {isPending && <p className="text-xs text-muted-foreground">Loading…</p>}

      {!isPending && (!data || data.length === 0) && (
        <p className="text-xs text-muted-foreground">No subcontractors assigned.</p>
      )}

      {data && data.length > 0 && (
        <div className="space-y-1.5">
          {data.map((a) => (
            <AssignmentRow key={a.id} assignment={a} />
          ))}
        </div>
      )}

      <JobAssignSubDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        jobId={jobId}
        contactId={contactId}
      />
    </div>
  );
}
